"use client"

import {
  Checkbox as CheckboxMUI,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material"

import styled from "@emotion/styled"
import { useEffect, useState } from "react"
import {
  Control,
  Controller,
  FieldValues,
  Path,
  useController,
} from "react-hook-form"
import CustomHelperText from "./CustomHelperText"
import Checkbox from "./icons/Checkbox"
import CheckboxActive from "./icons/CheckboxActive"
import ChevronDown from "./icons/ChevronDown"
import { CheckboxRow, StyledSelectMenuProps } from "./styles"

interface CustomTextFieldProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  type?: string
  multiline?: boolean
  rows?: number
  fullWidth?: boolean
  placeholder?: string
  isSubmitted?: boolean
  slotProps?: any
  sx?: any
  errors?: any
}

export const CustomTextField = <T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  type = "text",
  multiline = false,
  rows,
  fullWidth = true,
  isSubmitted = false,
  placeholder,
  slotProps,
  sx,
  errors,
}: CustomTextFieldProps<T>) => {
  const errorId = `${String(name)}-error`
  const hasError = !!errors?.[name]

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label={label}
          type={type}
          placeholder={placeholder}
          variant="outlined"
          error={hasError}
          helperText={
            <CustomHelperText
              render={isSubmitted}
              error={hasError}
              text={errors?.[name]?.message}
              id={errorId}
            />
          }
          multiline={multiline}
          rows={rows}
          slotProps={{
            ...slotProps,
            inputLabel: { shrink: true },
            htmlInput: {
              ...slotProps?.htmlInput,
              "aria-required": required ? "true" : undefined,
              "aria-describedby": hasError && isSubmitted ? errorId : slotProps?.htmlInput?.["aria-describedby"],
            }
          }}
          sx={{
            marginBlockEnd: "24px",
            ...(!multiline && {
              "@media (min-width: 1900px)": {
                "& .MuiInputBase-root": {
                  height: "48px",
                },
              },
            }),
            ...sx,
          }}
          fullWidth={fullWidth}
        />
      )}
    />
  )
}

interface CustomSelectProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  placeholder?: string
  fullWidth?: boolean
  isSubmitted?: boolean
  required?: boolean
  options: { value: number | string; label: string }[]
  sx?: any
  errors?: any
}

export const CustomSelect = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  fullWidth = true,
  isSubmitted = false,
  required = false,
  options,
  sx,
  errors,
}: CustomSelectProps<T>) => {
  const errorId = `${String(name)}-error`
  const hasError = !!errors?.[name]

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl
          fullWidth={fullWidth}
          error={hasError}
          sx={{
            marginBlockEnd: "24px",
            ...sx,
          }}
        >
          <InputLabel id={`${name}-label`} shrink>
            {label}
          </InputLabel>
          <Select
            {...field}
            variant="outlined"
            labelId={`${name}-label`}
            label={label}
            IconComponent={(props) => <ChevronDown {...props} />}
            MenuProps={StyledSelectMenuProps}
            displayEmpty
            inputProps={{
              "aria-required": required ? "true" : undefined,
              "aria-describedby": hasError && isSubmitted ? errorId : undefined,
            }}
            renderValue={(value: unknown) => {
              if (!value) {
                return (
                  // Rendered as a span so the Select's own text-overflow
                  // applies: a block child is clipped without an ellipsis,
                  // which hides the tail of longer placeholders
                  <Typography
                    color="gray"
                    variant="body2"
                    component="span"
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {placeholder}
                  </Typography>
                )
              }
              return <>{value}</>
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <CustomHelperText
            render={isSubmitted}
            error={hasError}
            text={errors?.[name]?.message}
            id={errorId}
          />
        </FormControl>
      )}
    />
  )
}

interface MultilineCustomTextFieldProps<T extends FieldValues>
  extends Omit<CustomTextFieldProps<T>, "multiline" | "rows"> {
  minRows?: number
  maxRows?: number
}

export const MultilineCustomTextField = <T extends FieldValues>({
  minRows = 3,
  maxRows = 6,
  ...props
}: MultilineCustomTextFieldProps<T>) => {
  const [count, setCount] = useState(0)
  const { field } = useController({ name: props.name, control: props.control })
  const maxLength = 250
  const remaining = maxLength - count

  useEffect(() => {
    setCount(field.value?.length || 0)
  }, [field.value])

  const fieldId = `${String(props.name)}-limit`

  return (
    <div style={{ position: "relative", marginBlockEnd: "24px" }}>
      <CustomTextField
        {...props}
        multiline
        rows={6}
        sx={{ marginBlockEnd: "0" }}
        slotProps={{
          input: {
            style: {
              resize: "vertical",
              fontSize: "16px",
              marginBlockEnd: "0",
            },
          },
          htmlInput: {
            maxLength,
            "aria-describedby": fieldId,
            "aria-label": `${props.label}, מקסימום ${maxLength} תווים, ${remaining} תווים נותרו`,
          },
        }}
      />
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          top: "-6px",
          right: "12px",
          backgroundColor: "white",
          padding: "0 8px",
          fontSize: "12px",
          color: "black",
        }}
      >
        {count}/{maxLength}
      </Typography>
      <span id={fieldId} aria-live="polite" className="sr-only">
        {remaining} תווים נותרו
      </span>
    </div>
  )
}

const StyledCheckbox = styled(CheckboxMUI, {
  shouldForwardProp: (prop) => prop !== "$error",
})<{ $error?: boolean }>`
  ${({ $error }) =>
    $error &&
    `
    rect{
      stroke: #d32f2f;}
  `}

  &.Mui-focusVisible {
    border: 1px dashed white;
    outline: 2px solid black;
    outline-offset: 2px;
    border-radius: 2px;
  }
`
interface CustomCheckboxProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  isSubmitted?: boolean
  errors?: any
}

export const CustomCheckbox = <T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  isSubmitted = false,
  errors,
}: CustomCheckboxProps<T>) => {
  const checkboxId = `checkbox-${String(name)}`
  const errorId = `${String(name)}-error`
  const hasError = !!errors?.[name]

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <>
            <CheckboxRow>
              <StyledCheckbox
                id={checkboxId}
                $error={hasError}
                {...field}
                icon={<Checkbox />}
                checkedIcon={<CheckboxActive />}
                checked={!!field.value}
                inputProps={{
                  "aria-label": label,
                  "aria-required": required ? "true" : undefined,
                  "aria-describedby": hasError && isSubmitted ? errorId : undefined,
                }}
                sx={{
                  marginRight: "-8px",
                  ":hover": {
                    backgroundColor: "transparent",
                  },
                }}
                onChange={(e) => {
                  field.onChange(e.target.checked)
                }}
              />
              <Typography
                variant="body2"
                component="label"
                htmlFor={checkboxId}
                sx={{ cursor: "pointer" }}
              >
                {label}
              </Typography>
            </CheckboxRow>
            <CustomHelperText
              render={isSubmitted}
              error={hasError}
              text={errors?.[name]?.message}
              id={errorId}
            />
          </>
        )
      }}
    />
  )
}
